import { type Customer } from '@hungryroot/customer';
import { Paragraph } from '@hungryroot/ramen';
import {
  LikeDislike,
  LikeDislikeVariant,
  LikeDislikeSize,
  type LikeDislikeTagsRatings,
} from '@hungryroot/ratings';
import { type TagGroups, useCustomerTagGroup } from '@hungryroot/tags';
import {
  usePatchCustomerTagSelf,
  type TagPayload,
} from '../../../../api/v2/customers/[id]/tags/patch/patch-customer-tags';
import {
  useTagGroup,
  type Tag,
} from '../../../../api/v2/tag_groups/get/v2-tag-groups-get';
import styles from './ControlTagBinary.module.css';

export enum TagPreference {
  Like = 1,
  NoRating = 0,
  Dislike = -1,
}

interface Props {
  testid?: string;
  customerId: Customer['id'];
  tagGroupId: TagGroups;
  showSubtitle?: boolean;
}

function getTagRating(tagId: number, array: TagPayload[]): TagPreference {
  const match = array.find(({ tag }) => {
    return tag === tagId;
  });

  if (match === undefined) {
    return TagPreference.NoRating;
  }

  return match.preference;
}

export function ControlTagBinary(props: Props) {
  const { testid, customerId, tagGroupId, showSubtitle } = props;
  const usePatchCustomerTagSelfMutation = usePatchCustomerTagSelf();

  const tagGroup = useTagGroup(tagGroupId);
  const { data: tagGroupData } = tagGroup || {};
  const queryTags = tagGroupData?.tags;

  const { data: customerTagGroupData } = useCustomerTagGroup(tagGroupId);
  const customerTags =
    customerTagGroupData?.results.map((obj) => {
      return {
        customer_id: customerId,
        preference: obj.preference,
        tag: obj.tag,
      };
    }) || [];

  function handleChange(tag: Tag, preference: TagPreference) {
    const payload = {
      customer_id: customerId,
      tag: tag.id,
      preference,
    };

    return usePatchCustomerTagSelfMutation.mutate({ data: [payload] });
  }

  const controlGroup = queryTags?.map((tag, index) => {
    const { id: tagId, image_url: imgSrc, name: tagName, remark } = tag;
    const tagCyid = `${testid}-toggle-tag-${tagId}`;

    return (
      <div className={styles.row} key={tagId}>
        <img
          src={imgSrc ? imgSrc : `/public/img/quiz/placeholder-image.png`}
          alt={tagName}
          className={styles.image}
        />
        <div className={styles.text}>
          <Paragraph>{tagName}</Paragraph>
          {showSubtitle && remark ? (
            <Paragraph testid={`${testid}-subtitle-${index}`}>
              {remark}
            </Paragraph>
          ) : null}
        </div>
        <LikeDislike
          key={tagId}
          testid={`${tagCyid}-binary-rating`}
          size={LikeDislikeSize.Small}
          rating={getTagRating(tagId, customerTags)}
          onRating={(value) => {
            handleChange(tag, value as LikeDislikeTagsRatings);
          }}
          variant={LikeDislikeVariant.Tag}
        />
      </div>
    );
  });

  return (
    <div data-testid={testid} className={styles.container}>
      {controlGroup}
    </div>
  );
}
